// Email HTML helpers: html -> text (like a copy/paste) and fabric image candidates paired with their reference.
import { parseFabricRefs, groupRefs, decodeEntities } from "./fabricUtils";

const BLOCK_TAGS = new Set(["P", "DIV", "TR", "LI", "UL", "OL", "TABLE", "TBODY", "THEAD", "H1", "H2", "H3", "H4", "H5", "H6", "SECTION", "ARTICLE", "HEADER", "FOOTER", "BLOCKQUOTE", "CENTER"]);
const SKIP_SRC = /(logo|icon|facebook|twitter|instagram|linkedin|youtube|pinterest|tiktok|spacer|pixel|track|badge|banner|signature)/i;
const SRC_ATTRS = ["src", "originalsrc", "data-src", "o:href", "data-imagesrc"];

function nodeText(root) {
  let out = "";
  const walk = (n) => {
    if (n.nodeType === 3) { out += n.nodeValue.replace(/\s+/g, " "); return; }
    if (n.nodeType !== 1) return;
    const tag = n.tagName.toUpperCase();
    if (tag === "BR" || tag === "HR") { out += "\n"; return; }
    if (tag === "SCRIPT" || tag === "STYLE") return;
    n.childNodes.forEach(walk);
    if (BLOCK_TAGS.has(tag)) out += "\n";
    else if (tag === "TD" || tag === "TH") out += "\t";
  };
  walk(root);
  return out;
}

// Outlook web wraps external links/images (safelinks, proxies): keep the original URL.
function unwrapUrl(value) {
  const v = decodeEntities((value || "").trim());
  if (v.startsWith("data:image/")) return v;
  if (!/^https?:\/\//i.test(v)) return null;
  try {
    const u = new URL(v);
    if (/safelinks|outlook|office|live\.com/i.test(u.hostname)) {
      for (const p of ["url", "u"]) {
        const inner = u.searchParams.get(p);
        if (inner && /^https?:\/\//i.test(inner)) return inner;
      }
    }
  } catch { return null; }
  return v;
}

function usableSrc(img, rawHtml) {
  for (const a of SRC_ATTRS) {
    const u = unwrapUrl(img.getAttribute(a));
    if (u) return u;
  }
  const shapeId = img.getAttribute("v:shapes");
  if (shapeId && rawHtml) {
    const esc = shapeId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = rawHtml.match(new RegExp(`id="${esc}"[\\s\\S]{0,1500}?o:href="(https?://[^"]+)"`));
    if (m) return unwrapUrl(m[1]);
  }
  return null;
}

export function htmlToText(html) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  doc.querySelectorAll("script,style,link,meta,title,img,iframe,object,embed,video,audio,svg,form,input,button").forEach((n) => n.remove());
  doc.querySelectorAll("*").forEach((el) => [...el.attributes].forEach((a) => {
    if (/^on/i.test(a.name) || ["src", "href", "background", "srcset"].includes(a.name.toLowerCase())) el.removeAttribute(a.name);
  }));
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-100000px;top:0;width:900px;opacity:0;pointer-events:none;";
  host.innerHTML = doc.body.innerHTML;
  document.body.appendChild(host);
  const text = host.innerText ?? host.textContent;
  host.remove();
  return text.replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

// Each image is paired with the fabric reference of the smallest enclosing block that mentions a single fabric.
export function extractEmailImageCandidates(html, extraImages = []) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  doc.querySelectorAll("script,style").forEach((n) => n.remove());
  const refs = groupRefs(parseFabricRefs(nodeText(doc.body)));
  // An image whose URL contains a fabric code (e.g. docril_solid_colors_00683_logo-red.jpg) is that fabric's photo.
  const refInUrl = (src) => (src.startsWith("data:") ? null : refs.find((r) => new RegExp(`(^|[^A-Za-z0-9])${r.code}([^A-Za-z0-9]|$)`, "i").test(src)));
  let unavailable = 0;
  const found = [];
  doc.querySelectorAll("img").forEach((img) => {
    const src = usableSrc(img, html);
    if (!src) { unavailable += 1; return; }
    const urlRef = refInUrl(src);
    if (urlRef) { found.push({ src, key: urlRef.key, level: 0, filename: img.getAttribute("alt") || "" }); return; }
    const w = parseInt(img.getAttribute("width"), 10);
    const h = parseInt(img.getAttribute("height"), 10);
    if ((w && w < 40) || (h && h < 40) || (!src.startsWith("data:") && SKIP_SRC.test(src))) return;
    let el = img.parentElement;
    let level = 1;
    let key = null;
    while (el) {
      const blockRefs = parseFabricRefs(nodeText(el));
      if (blockRefs.length) {
        if (new Set(blockRefs.map((r) => r.key)).size === 1) key = blockRefs[0].key;
        break;
      }
      el = el.parentElement;
      level += 1;
    }
    found.push({ src, key, level, filename: img.getAttribute("alt") || "" });
  });

  const best = {};
  found.forEach((f) => { if (f.key) best[f.key] = Math.min(best[f.key] ?? Infinity, f.level); });
  const seen = new Set();
  const items = [];
  found.forEach((f) => {
    const key = f.key && f.level === best[f.key] ? f.key : null;
    const id = `${key}::${f.src}`;
    if (seen.has(id)) return;
    seen.add(id);
    items.push({ id: `img-${items.length}`, src: f.src, key, filename: f.filename });
  });
  extraImages.forEach((img) => items.push({ id: `img-${items.length}`, src: img.data_url, key: null, filename: img.filename || "" }));
  return { items, refs, unavailable };
}
